import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Spinner } from '@glplus/glpweb-dsl';
import { hasSession } from '../lib/authFlow';

/**
 * Portal guard — own implementation (isolation ruling: no shared-libs
 * usePortalGuard). Pool membership IS the perimeter: any valid Runner-pool
 * session is admitted; what the account may do is decided server-side by
 * runner-api from the profile's lifecycle. No session → /login?next=.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const [state, setState] = useState<'checking' | 'ok' | 'redirect'>('checking');

  useEffect(() => {
    let alive = true;
    void hasSession().then((live) => {
      if (!alive) return;
      if (live) setState('ok');
      else {
        setState('redirect');
        const next = encodeURIComponent(`${pathname}${search}`);
        window.location.assign(`/login?next=${next}`);
      }
    });
    return () => {
      alive = false;
    };
  }, [pathname, search]);

  if (state !== 'ok') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
        <Spinner aria-label={state === 'checking' ? t('app.checkingAccess') : t('app.redirecting')} />
      </Box>
    );
  }
  return <>{children}</>;
}
